import { useState } from 'react'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { DataTable, type Column } from '@/components/ui/DataTable'
import { Modal } from '@/components/ui/Overlay'
import { EmptyState } from '@/components/ui/States'
import { cn } from '@/lib/cn'
import type { ForwardRow, ReverseRow } from './translations'

type Direction = 'forward' | 'reverse'

const unmapped = <span className="text-[12px] text-ink-faint">Unmapped</span>

const forwardColumns: Column<ForwardRow>[] = [
  {
    key: 'wbProgram',
    header: 'Program display value',
    sortBy: (r) => r.wbProgram.toLowerCase(),
    render: (r) => <span className="font-head text-[13px] font-semibold">{r.wbProgram}</span>,
  },
  {
    key: 'slateName',
    header: 'Slate prompt (mapping to)',
    width: '420px',
    sortBy: (r) => r.slateName ?? '',
    render: (r) => (r.slateName === null ? unmapped : <span className="text-[12px]">{r.slateName}</span>),
  },
]

const reverseColumns: Column<ReverseRow>[] = [
  {
    key: 'slateName',
    header: 'Slate prompt',
    sortBy: (r) => r.slateName.toLowerCase(),
    render: (r) => <span className="text-[12px]">{r.slateName}</span>,
  },
  {
    key: 'wbProgram',
    header: 'Mapping to (WB program)',
    width: '300px',
    sortBy: (r) => r.wbProgram ?? '',
    render: (r) =>
      r.wbProgram === null ? (
        unmapped
      ) : (
        <span className="text-[12px]">
          <span className="font-head font-semibold">{r.wbProgram}</span>
        </span>
      ),
  },
]

/** Read-only preview of the two lists the export writes, one tab per direction. Unmapped rows included (PC-27). */
export function TranslationsModal({
  open,
  forward,
  reverse,
  onClose,
  onExport,
}: {
  open: boolean
  forward: ForwardRow[]
  reverse: ReverseRow[]
  onClose: () => void
  onExport: () => void
}) {
  const [direction, setDirection] = useState<Direction>('forward')

  const tabs: Array<{ value: Direction; label: string; count: number; unmapped: number }> = [
    {
      value: 'forward',
      label: 'WB program → Slate',
      count: forward.length,
      unmapped: forward.filter((r) => r.slateName === null).length,
    },
    {
      value: 'reverse',
      label: 'Slate → WB program',
      count: reverse.length,
      unmapped: reverse.filter((r) => r.wbProgram === null).length,
    },
  ]

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Translations table"
      description="Every offered WB program and enabled slate program, mapped or not. Disabled slate programs are left out."
      width="max-w-[1000px]"
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Close
          </Button>
          <Button size="sm" variant="primary" onClick={onExport} disabled={forward.length + reverse.length === 0}>
            <Download size={14} aria-hidden />
            Export CSV
          </Button>
        </>
      }
    >
      <div role="tablist" aria-label="Mapping direction" className="mb-3 inline-flex gap-1 rounded-md bg-surface-sunk p-0.5">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={direction === tab.value}
            onClick={() => setDirection(tab.value)}
            className={cn(
              'flex items-center gap-1.5 rounded px-2.5 py-1 font-head text-[12px] font-semibold transition-colors',
              direction === tab.value ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink',
            )}
          >
            {tab.label}
            <span className="tabular font-normal text-ink-faint">
              {tab.count}
              {tab.unmapped > 0 && ` · ${tab.unmapped} unmapped`}
            </span>
          </button>
        ))}
      </div>
      <div className="max-h-[60vh] overflow-y-auto">
        {direction === 'forward' ? (
          <DataTable
            rows={forward}
            columns={forwardColumns}
            rowKey={(r) => r.wbProgram}
            empty={<EmptyState title="No programs" />}
          />
        ) : (
          <DataTable
            rows={reverse}
            columns={reverseColumns}
            rowKey={(r) => r.slateName}
            empty={<EmptyState title="No enabled slate programs" />}
          />
        )}
      </div>
    </Modal>
  )
}
